import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface SyncComparisonBadgeProps {
  /** Number of locations synced to Bridge */
  syncedCount: number;
  /** Total number of listings in the source system (e.g., Guesty) */
  sourceCount: number | null | undefined;
  /** When the source count was last updated */
  sourceCountUpdatedAt?: string | null;
  /** Display variant: 'badge' (default) or 'compact' (fraction style) */
  variant?: 'badge' | 'compact';
  /** Additional CSS classes */
  className?: string;
}

/**
 * Displays a comparison between synced locations and source system total.
 * - 'badge' variant: Shows "X of Y synced (Z%)" in a colored badge
 * - 'compact' variant: Shows "X/Y (Z%)" as large text with tooltip
 */
export function SyncComparisonBadge({
  syncedCount,
  sourceCount,
  sourceCountUpdatedAt,
  variant = 'badge',
  className,
}: SyncComparisonBadgeProps): JSX.Element {
  // If source count is not available, show only synced count
  if (sourceCount == null) {
    return (
      <span
        className={cn(
          variant === 'compact' ? 'text-2xl font-bold' : 'text-sm font-medium',
          className
        )}
        data-testid="sync-comparison-simple"
      >
        {String(syncedCount)}{variant === 'badge' ? ' synced' : ''}
      </span>
    );
  }

  const isSynced = syncedCount === sourceCount;
  const percentage =
    sourceCount > 0 ? Math.round((syncedCount / sourceCount) * 100) : 100;

  const lastUpdated = sourceCountUpdatedAt
    ? new Date(sourceCountUpdatedAt).toLocaleString()
    : 'Unknown';

  const tooltipContent = (
    <>
      <p>
        {isSynced
          ? 'All listings from partner are synced'
          : `${String(sourceCount - syncedCount)} listings not yet synced`}
      </p>
      <p className="text-xs text-muted-foreground">
        Source count updated: {lastUpdated}
      </p>
    </>
  );

  // Compact variant: "5/920 (1%)" as large text - matches other stat cards
  if (variant === 'compact') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={cn('text-2xl font-bold cursor-default', className)}
              data-testid="sync-comparison-compact"
            >
              {String(syncedCount)}/{String(sourceCount)}
              {!isSynced && (
                <span className={cn(
                  'ml-2 text-sm font-normal',
                  percentage < 50 ? 'text-amber-600' : 'text-muted-foreground'
                )}>
                  ({String(percentage)}%)
                </span>
              )}
              {isSynced && (
                <span className="ml-2 text-sm text-green-600">✓</span>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent>{tooltipContent}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Badge variant (default): "5 of 920 synced (1%)" in colored badge
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
              isSynced && 'bg-green-100 text-green-800',
              !isSynced && 'bg-amber-100 text-amber-800',
              className
            )}
            data-testid="sync-comparison-badge"
          >
            {String(syncedCount)} of {String(sourceCount)} synced
            {!isSynced && ` (${String(percentage)}%)`}
          </span>
        </TooltipTrigger>
        <TooltipContent>{tooltipContent}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
